import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Send } from 'lucide-react';
import { useChat } from '@/contexts/ChatContext';
import { Input } from '@/components/ui/input';

const MAX_LENGTH = 200;
const MAX_SUGGESTIONS = 8;

/** Formats remaining milliseconds into a human-readable countdown string. */
function formatCountdown(ms: number): string {
  if (ms <= 0) return '';
  const totalSeconds = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

const SuspensionTimer: React.FC<{ expiresAt: string }> = ({ expiresAt }) => {
  const [remaining, setRemaining] = useState(() => new Date(expiresAt).getTime() - Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(new Date(expiresAt).getTime() - Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (remaining <= 0) return null;

  return (
    <div className="flex flex-col items-center gap-1 px-3 py-3 border-t border-border bg-destructive/5">
      <div className="flex items-center gap-1.5">
        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 text-destructive flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="8" r="6.5" />
          <path d="M8 4.5V8.5L10.5 10" />
        </svg>
        <span className="text-xs font-medium text-destructive">Suspended from chat</span>
      </div>
      <span className="text-[11px] text-muted-foreground">
        You can chat again in <span className="font-semibold text-foreground">{formatCountdown(remaining)}</span>
      </span>
    </div>
  );
};

/** Extract the @mention query from cursor position. Returns null if not in a mention. */
function getMentionQuery(text: string, cursorPos: number): { query: string; start: number } | null {
  // Walk backwards from cursor to find the last '@'
  let i = cursorPos - 1;
  while (i >= 0) {
    if (text[i] === '@') {
      const query = text.slice(i + 1, cursorPos);
      // If there's a space in the query, the mention is terminated
      if (/\s/.test(query)) return null;
      return { query: query.toLowerCase(), start: i };
    }
    if (/\s/.test(text[i])) return null;
    i--;
  }
  return null;
}

export const ChatInput: React.FC = () => {
  const { sendMessage, isConnected, hasAcceptedRules, isSuspended, suspensionExpiresAt, messages } = useChat();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<{ query: string; start: number } | null>(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const mentionRef = useRef<HTMLDivElement>(null);

  // Derive unique usernames from recent messages (deduplicated, most recent first)
  const recentUsernames = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    // Iterate from newest to oldest
    for (let i = messages.length - 1; i >= 0; i--) {
      const u = messages[i].username;
      if (!seen.has(u)) {
        seen.add(u);
        result.push(u);
      }
    }
    return result;
  }, [messages]);

  // Filter usernames based on mention query
  const suggestions = useMemo(() => {
    if (!mentionQuery) return [];
    const q = mentionQuery.query;
    return recentUsernames
      .filter(u => u.toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [mentionQuery, recentUsernames]);

  const updateMentionState = useCallback((value: string, cursorPos: number) => {
    const mq = getMentionQuery(value, cursorPos);
    setMentionQuery(mq);
    setShowMentions(!!mq && recentUsernames.length > 0);
    setSelectedIdx(0);
  }, [recentUsernames]);

  const insertMention = useCallback((username: string) => {
    if (!mentionQuery || !inputRef.current) return;
    const before = text.slice(0, mentionQuery.start);
    const after = text.slice(inputRef.current.selectionStart || text.length);
    const newText = `${before}@${username} ${after}`.slice(0, MAX_LENGTH);
    setText(newText);
    setShowMentions(false);
    setMentionQuery(null);
    // Focus and move cursor after the inserted mention
    requestAnimationFrame(() => {
      if (inputRef.current) {
        const pos = before.length + username.length + 2; // @username + space
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos, pos);
      }
    });
  }, [mentionQuery, text]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || isSending || !isConnected || isSuspended) return;

    setIsSending(true);
    setError(null);
    setShowMentions(false);

    const result = await sendMessage(trimmed);
    if (result.success) {
      setText('');
    } else {
      setError(result.error || 'Failed to send message');
      setTimeout(() => setError(null), 3000);
    }
    setIsSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx(prev => (prev + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx(prev => (prev - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        insertMention(suggestions[selectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.slice(0, MAX_LENGTH);
    setText(val);
    updateMentionState(val, e.target.selectionStart || val.length);
  };

  const handleClick = () => {
    if (inputRef.current) {
      updateMentionState(text, inputRef.current.selectionStart || text.length);
    }
  };

  const isNearLimit = text.length > MAX_LENGTH * 0.8;
  const isOverLimit = text.length > MAX_LENGTH;

  if (!hasAcceptedRules) {
    return (
      <div className="px-3 py-2 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">Accept the chat rules to start chatting</p>
      </div>
    );
  }

  // Show suspension timer for temporary suspensions
  if (isSuspended && suspensionExpiresAt) {
    return <SuspensionTimer expiresAt={suspensionExpiresAt} />;
  }

  // Show permanent ban message
  if (isSuspended && !suspensionExpiresAt) {
    return (
      <div className="flex items-center justify-center gap-1.5 px-3 py-3 border-t border-border bg-destructive/5">
        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 text-destructive flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="8" r="6.5" />
          <path d="M5 5l6 6M11 5l-6 6" />
        </svg>
        <span className="text-xs font-medium text-destructive">You have been banned from chat</span>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-t border-border">
      {error && (
        <div className="text-[11px] text-destructive mb-1.5 px-1 leading-tight">{error}</div>
      )}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          {/* Mention autocomplete dropdown */}
          {showMentions && suggestions.length > 0 && (
            <div
              ref={mentionRef}
              className="absolute bottom-full left-0 right-0 mb-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50"
            >
              <div className="py-1 max-h-[200px] overflow-y-auto">
                {suggestions.map((username, idx) => (
                  <button
                    key={username}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                      insertMention(username);
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors ${
                      idx === selectedIdx ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <span className="text-muted-foreground">@</span>
                    <span className="font-medium truncate">{username}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <Input
            ref={inputRef}
            type="text"
            value={text}
            onChange={handleChange}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            placeholder={isConnected ? 'Type your message...' : 'Connecting...'}
            disabled={!isConnected || isSending}
            maxLength={MAX_LENGTH}
            className="w-full h-9 bg-muted/50 border-border rounded-lg px-3 py-2 md:text-xs focus:ring-1 focus:ring-primary/50 focus:ring-offset-0"
          />
          {isNearLimit && (
            <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] ${isOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
              {text.length}/{MAX_LENGTH}
            </span>
          )}
        </div>
        <button
          onClick={handleSend}
          disabled={!text.trim() || !isConnected || isSending || isOverLimit}
          className="flex-shrink-0 h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-opacity active:scale-95"
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};
